const coinIcon = new URL('../assets/coin.png', import.meta.url).toString()

export type Balance = {
    usdc: number
    point: number
    airdrop: number
    airdropExpiredAt?: string
    totalInGame: number
}

export type Currency = {
    name: string
    img: string
    decimals?: number
    gradient?: string
    disabled?: boolean
    blockchain?: string
    /** Offered as a way to add funds. */
    deposit?: boolean
    deposit_type?: number
    moonpay?: boolean
    /**
     * Offered as a payout target. Split out from `disabled` because that one flag was carrying two
     * unrelated meanings — it gated withdrawals while `deposit` gated deposits, so neither name
     * told you which action it applied to.
     */
    withdraw?: boolean
    /** What the user is shown. `name` stays the wire value sent to the API. */
    label?: string
    /** One short line under the label, e.g. the network or the provider. */
    note?: string
    /** Render a card glyph rather than `img` — for providers that are a payment method, not a coin. */
    cardIcon?: boolean
}

export const Br1TokenCurrency: Currency = {
    name: 'BR1TOKEN',
    img: coinIcon,
    decimals: 2,
}

export type Validator = (value: any) => boolean

export type WithdrawStrategy = {
    currency: string
    /** Fraction, not a percentage: 0.015 is 1.5%. */
    fee: number
    min_amount: number
    rates: number
}

type WithdrawRequestStatus = {
    status: string
    created_at: string
    created_by: string
}

export type WithdrawTriageVerdict = 'cleared' | 'review' | 'hold'

export type WithdrawTriageSignal = {
    key: string
    ok: boolean
    text: string
    escalates_to: WithdrawTriageVerdict | null
    /** Whether this check actually moved the verdict. A failing check that
     *  escalates to nothing, or one suppressed because the account is taking
     *  back its own float, is not a reason anything stopped. */
    counted: boolean
    /** Why a check that fired was not counted, when it was suppressed
     *  because the account is only taking back what it deposited. */
    suppressed_because?: string | null
    data: Record<string, unknown>
}

/**
 * Read-only review context. Absent when WITHDRAW_TRIAGE_ENABLED is off, so
 * every consumer has to treat it as optional -- a verdict is a suggestion for
 * the reviewer, never a precondition for rendering the row.
 */
/**
 * The integrity system's reading of this account, carried beside the verdict.
 *
 * `cheated` is deliberately NOT "the score is low". It is true only on
 * evidence: a cheat report a human marked actioned, or a detection from a
 * detector we trust. A low score is a ranking, and telling a reviewer somebody
 * cheated on the strength of a ranking is the mistake the rest of this system
 * exists to avoid.
 */
export type WithdrawIntegrity = {
    /** 0-100, where 100 is clean. Null when the account has no profile yet. */
    score: number | null
    /**
     * False once a reviewer has dismissed every report on the account, even
     * when a detector still reads as conclusive. A person overruling the
     * machine is the strongest signal available here, in both directions.
     */
    cheated: boolean
    actioned_reports: number
    trusted_detections: number
    /** The integrity gate is currently stopping this payout being approved. */
    held_by_gate: boolean
    /** Reports raised and not dismissed, actioned ones included. */
    open_reports: number
    /**
     * Every report on this account was read and dismissed. Distinct from having
     * no reports at all: this account was investigated and cleared, and saying
     * so is what stops it being investigated again next week.
     */
    cleared_by_review: boolean
    /**
     * Hit-level aim accuracy was scored. False means too few hits: the score
     * cannot have lost points for aim, so 100 is "not measured", not "clean".
     */
    accuracy_scored: boolean
    /** What the score rests on and when it was made. See team_tools/integrityCopy. */
    sessions?: number | null
    computed_at?: string | null
    weights_version?: string | null
}

/**
 * What this team already decided about this account.
 *
 * Context, never a verdict input: the backend reports these and lets them
 * suppress nothing, so nine clean payouts cannot buy the tenth one a pass.
 * What it is for is the reviewer who would otherwise re-run an investigation
 * they already finished last week.
 */
export type WithdrawHistory = {
    /** Withdrawals from this account that were finalized. */
    approved: number
    declined: number
    /** Every request from this account in any status, this one included. */
    requests?: number
    /** Total already paid to this account, in-game USDC. */
    paid: string
    last_decision: 'approved' | 'declined' | null
    last_decided_at: string | null
    last_decided_by: string | null
}

export type WithdrawTriage = {
    verdict: WithdrawTriageVerdict
    integrity?: WithdrawIntegrity | null
    history?: WithdrawHistory | null
    headline: string
    signals: WithdrawTriageSignal[]
    facts: {
        deposited: string
        airdropped: string
        /** Deposits plus airdrops: money that entered the account from outside. */
        funded: string
        /** Funded plus winnings: everything the ledger can source. */
        accounted: string
        /**
         * USDC that arrived by an operation the triage cannot name. Should be
         * "0.00"; anything else means our own books do not add up.
         */
        unexplained: string
        withdrawn: string
        /** The in-game debit, not the fee-reduced target amount on the request. */
        this_request: string
        earned: string
        /** Kill-race winnings, a SUBSET of `earned` rather than a sibling. */
        kill_race: string
        /** Spawn-fee share from kills. Also a subset of `earned`. */
        kills_earned: string
        /** Loot, treasure hunts, races, referrals: earned minus the two above. */
        other_earned: string
        /** How much of the balance BR1 paid for itself: every winning that is
         *  not the spawn-fee share of a kill, plus airdrops. Kill winnings and
         *  deposits are other people's money, so neither is in here. See
         *  `withdraw_triage._house_funded`. */
        house_funded: string
        /** The service fee BR1 kept out of their winnings, summed per session.
         *  Not 20% of anything -- the rate is 20% less whatever retention
         *  bonus their kit carried that match. */
        service_fee_kept: string
        /** Every real USDC path between BR1 and this account, both ways.
         *  `house_net` is `house_in - house_out`. Deposits, USDC spawn fees
         *  and match winnings are absent on purpose: those are players paying
         *  each other. See `withdraw_triage._assemble`. */
        store_spend: string
        pack_spend: string
        marketplace_fees: string
        withdraw_fees: string
        withheld: string
        /** Their spawn fees paid in promo currency. Winnings are paid in
         *  USDC, so promo spent on a fee is a real cost to BR1. */
        promo_fees: string
        /** Airdrop credit received, by what it was for. */
        airdrop_received?: string
        airdrop_level_rewards?: string
        airdrop_deposit_match?: string
        airdrop_manual?: string
        airdrop_other?: string
        /** The live USDC balance: the sum of every USDC row on the account.
         *  Not a reconstruction -- the balance table is maintained by a
         *  trigger over exactly these rows. */
        balance: string
        house_in: string
        house_out: string
        house_net: string
        /** Tokens held on chain, and the split by kind. */
        nfts_held: number
        nfts_by_type: Record<string, number>
        /** The best earnings-retention bonus they have actually played with,
         *  as a fraction. Subtracted from BR1's 20% service fee, so 0.15 means
         *  they keep 95c of every dollar won rather than 80c. Null when they
         *  have never played, which is not a bonus of zero. */
        retention_bonus: number | null
        cheat_sessions: number
        cheat_reports_open: number
        /** Read by a human who neither actioned nor dismissed it. Not "unread". */
        cheat_reports_reviewed: number
        cheat_reports_actioned: number
        /** 0-1. Compare against headshot_baseline, not against a fixed number. */
        headshot_rate: number
        headshot_baseline: number
        /** The counts behind headshot_rate, over the same 30 days as the baseline. */
        headshot_kills?: number
        headshot_kills_measured?: number
        /** Below this many kills the rate is not judged. */
        headshot_min_kills?: number
        fees_paid: string
        net_after_this: string
        own_money: boolean
        opponents: number
        kills: number
        /** Accounts on this IP that have themselves requested a withdrawal.
         *  The only one of the two that escalates. */
        ip_twins: number
        /** Every account on this IP, which is what the Accounts page counts. */
        ip_neighbours: number
        /** How many addresses hold three or more of this account's victims. */
        victim_ip_clusters: number
        /** The worst of them: the address, how many victims, and their names. */
        victim_ip_worst: {
            ip: string
            victims: number
            /** Kills this account dealt to the ring. Same value as `dealt`,
             *  under the name every existing reader already uses. */
            kills: number
            /** Kills this account dealt to the ring's members. */
            dealt: number
            /** Kills the ring's members dealt back to this account. */
            taken: number
            usernames: string[]
        /** Each victim, with the kills that ran to and from them. */
        victim_rows: Array<{ username: string; dealt: number; taken: number }>
        } | null
        /** Kills traded, either direction, with accounts on this account's own IP. */
        same_ip_kills: number
        /** How many distinct same-IP accounts those kills involve. */
        same_ip_accounts: number
        /** Of those, the ones this account dealt. */
        same_ip_dealt: number
        /** Of those, the ones dealt to it. */
        same_ip_taken: number
        /** Who, and which way round, busiest first. */
        same_ip_opponents: Array<{ id: number; username: string; dealt: number; taken: number }>
        /** Names of the withdrawing accounts on this IP. */
        ip_twin_names: string[]
        /** Names of every account on this IP. */
        ip_neighbour_names: string[]
        /** Which address(es) each of those was actually met on, keyed by name.
         *
         *  The row named the neighbour and stopped, so "shares an IP with" could
         *  not distinguish one shared address from six -- and on an account with
         *  ten addresses that is the difference between a household and a
         *  coincidence. Capped at four an account; past that the answer is
         *  "lots" and the account page holds the full list. */
        ip_neighbour_addresses: Record<string, string[]>
        /*  The same figures for the MACHINE the client reported, named in
         *  parallel with the address ones so the two can be read side by side.
         *
         *  They exist because a VPN empties the address half: two accounts
         *  behind one exit node share no address and read as strangers. A
         *  machine is what a VPN does not change.
         *
         *  One-directional evidence, everywhere. The hwid comes from a client
         *  running on hardware the player controls, so a match says something
         *  and an absence says nothing -- never render "0 shared machines" as
         *  though it cleared the account. `devices_reported` is what tells the
         *  two apart. */
        /** Accounts on this machine that have themselves requested a withdrawal. */
        device_twins: number
        /** Every account on this machine, withdrawing or not. */
        device_neighbours: number
        /** How many usable machines this account itself reported. Zero means we
         *  have NO reading -- an old client, or a farm randomising its hwid --
         *  and never that the account is alone on its PC. */
        devices_reported: number
        /** How many machines hold two or more of this account's victims. */
        victim_device_clusters: number
        /** The worst of them. `hwid_short` is the first 12 characters, which is
         *  what a screen shows; the full value is there for comparing two. */
        victim_device_worst: {
            hwid: string
            hwid_short: string
            victims: number
            kills: number
            dealt: number
            taken: number
            usernames: string[]
        /** Each victim, with the kills that ran to and from them. */
        victim_rows: Array<{ username: string; dealt: number; taken: number }>
        } | null
        /** Kills traded, either direction, with accounts on this account's own machine. */
        same_device_kills: number
        /** How many distinct same-machine accounts those kills involve. */
        same_device_accounts: number
        /** Of those, the ones this account dealt. */
        same_device_dealt: number
        /** Of those, the ones dealt to it. */
        same_device_taken: number
        /** Who, and which way round, busiest first. */
        same_device_opponents: Array<{
            id: number
            username: string
            dealt: number
            taken: number
        }>
        /** Names of the withdrawing accounts on this machine. */
        device_twin_names: string[]
        /** Names of every account on this machine. */
        device_neighbour_names: string[]
        /** Each account on this machine: who, how much they play, and the
         *  kills that ran between them and the account being reviewed. */
        device_neighbour_rows: Array<{
            username: string
            sessions: number
            kills: number
            deaths: number
        }>
        sessions: number
        account_age_days: number | null
    }
}

/**
 *  Why this row cannot be approved, or absent when it can.
 *
 *  Deliberately not part of `triage`. Triage is a suggestion, and every
 *  component that renders it returns null when it is missing -- which is exactly
 *  what must NOT happen to a refusal. Staff-only: the server sends it only with
 *  `include_staff_detail`, because it names what a person concluded about the
 *  account whose own list reads the same DTO.
 */
export type WithdrawPayoutBlock = {
    blocked: boolean
    /** Which checks disqualified it. Stable keys, safe to switch on. */
    codes: Array<
        | 'cheat_history'
        | 'cluster'
        | 'same_ip_kills'
        | 'same_device_kills'
        | 'shared_identity'
        | 'net_position'
        | 'bonus_unwagered'
    >
    /** The same checks as sentences, in the reviewer's own vocabulary. */
    reasons: string[]
}

export type WithdrawRequest = {
    uuid: string
    wallet: string
    status: string
    created_by: string
    created_at: string
    updated_at: string
    currency: string
    amount: number
    payout_block?: WithdrawPayoutBlock | null
    token_amount: number
    status_history: WithdrawRequestStatus[]
    /** On a decline response: the player's other queued requests that were
     *  declined along with it, by uuid. Absent on every other response. */
    also_declined?: string[]
    /** Decisions already made about the account: the ban stopping it, and any
     *  staff tags. Staff-only — absent from the player-facing list. */
    account_flags?: {
        ban?: { reason: string | null; permanent: boolean; kind?: 'ban' | 'pause' } | null
        tags?: string[]
        /** Who added each tag, by tag. */
        tag_by?: Record<string, string | null>
        /** Holds the server refuses Approve and Pay now on. Sent with the row,
         *  not with the triage, so they show even when the triage is off. */
        frozen?: { reason: string | null } | null
        gate_hold?: { reason: string } | null
    } | null
    blockchain_tx: string
    blockchain_tx_url: string
    user_id: number
    payout_error?: string
    approve_comment?: string
    /** Why it was declined, apart from the approver's note (D-108). Staff-only. */
    decline_reason?: string
    triage?: WithdrawTriage | null
    /** Set by the page, never by the server: this row's verdict and payout
     *  block have been asked for and have not arrived yet. Approve waits on it,
     *  because a missing `payout_block` would otherwise read as "not blocked". */
    verdict_pending?: boolean
    /** The server ran this row's checks and they threw, so "no verdict, no
     *  block" means "not checked", not "nothing found". */
    checks_failed?: boolean
}

/** `bank.withdraw_request.verdicts`: the half of each row the list leaves out. */
export type WithdrawVerdicts = Record<
    string,
    { triage: WithdrawTriage | null; payout_block: WithdrawPayoutBlock | null; checks_failed?: boolean }
>

export type WithdrawRequestsParams = {
    page?: number
    page_size?: number
    filters?: {
        status?: string
        username?: string
        /** One request. */
        uuid?: string
        /** Confirmed fraud clusters only. */
        fraud?: boolean
        /** Triage verdict. Not a column -- see `_by_verdict`; the queue is
         *  scanned up to a cap and the response says if it was truncated. */
        verdict?: 'all' | 'cleared' | 'review' | 'hold'
    }
}

export type WithdrawUserRequestsParams = {
    page?: number
    filters?: {
        status?: 'all' | 'submitted' | 'processing' | 'completed'
    }
}

export type WithdrawRequestListResponse = {
    count: number
    page_size: number
    page: number
    pages: number
    data: WithdrawRequest[]
    /** How many requests the verdict filter actually looked at. */
    verdict_scanned?: number | null
    /** It hit the cap, so there are older ones it did not reach. */
    verdict_truncated?: boolean
    /** Triage is off or it failed, so the verdict filter cannot mean anything. */
    verdict_unavailable?: boolean
}

export type WithdrawRequestSummary = {
    all: number
    created: number
    approved: number
    processing: number
    finalized: number
    declined?: number
    /** How the WAITING queue splits across the three verdicts. Counted over
     *  the waiting rows whatever the list is filtered to, so these three sum
     *  to `created` unless `verdict_truncated`. */
    cleared: number
    review: number
    hold: number
    /** The waiting queue was longer than the triage scan cap, or triage is
     *  unavailable, so the three above are not the whole of it. */
    verdict_truncated: boolean
}

export type PayoutWalletBalance = {
    pubkey: string
    currency: string
    amount: number
    updated_at: string
    /** What the waiting queue (requested + approved) would pay out of it. */
    owed?: number | null
    owed_requests?: number | null
}

export type EarningFeeStatistic = {
    total: number
    hours_24: number
    days_7: number
    days_30: number
    today: number
    week: number
    month: number
}

export type EarningFeeHistory = {
    days: number
    /** Date string -> fees collected that day, oldest first. */
    history: Record<string, number>
}
